/** The proposed title never goes over this many characters when it comes from the text. */
export const LONGUEUR_MAX_TITRE = 60;

/**
 * How much of the start of the text is read to propose a title: far more than 60 characters
 * once the spaces are squeezed, unless the text opens on a long run of blanks inside its words.
 */
const DEBUT_LU = 2_000;

/**
 * A file name that says nothing about the document: "Document1", "Sans titre", "scan_004",
 * "IMG_2034"... one of the generic words, with or without a number.
 */
const NOM_GENERIQUE =
  /^(?:document|doc|sans titre|untitled|nouveau document|scan|img|image)(?:[\s-]*\(?\d+\)?)?$/i;

/** Punctuation (and dashes) dropped from the end of a title cut out of the text. */
const PONCTUATION_FINALE = /[\s.,;:!?\u{2026}\-\u{2013}\u{2014}]+$/u;

/** A file name without its extension, underscores read as spaces; "" when nothing is left. */
function titreDepuisNom(nom: string): string {
  return nom
    .replace(/\.[A-Za-z0-9]{1,5}$/, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The first words of a text: whitespace and line breaks squeezed to one space, cut at the
 * last whole word that fits in 60 characters, final punctuation removed.
 */
function titreDepuisTexte(texte: string): string {
  // Only the start of the text can make the title: a text of several megabytes (B2, #13) is not
  // squeezed whole at every keystroke.
  const debut = texte.trimStart().slice(0, DEBUT_LU);
  const signes = Array.from(debut.replace(/\s+/g, ' ').trim());
  let coupe = signes;
  if (signes.length > LONGUEUR_MAX_TITRE) {
    coupe = signes.slice(0, LONGUEUR_MAX_TITRE);
    // Cut in the middle of a word: that word goes. A single word longer than the limit is
    // cut where it stands, there is no whole word to keep.
    if (signes[LONGUEUR_MAX_TITRE] !== ' ') {
      const dernierEspace = coupe.lastIndexOf(' ');
      if (dernierEspace > 0) coupe = coupe.slice(0, dernierEspace);
    }
  }
  return coupe.join('').replace(PONCTUATION_FINALE, '');
}

/**
 * The title Rosetta proposes while the user has typed none: the name of the file the text
 * comes from (extension dropped, underscores read as spaces) unless that name is generic,
 * otherwise the first words of the text. "" when the text is empty or only punctuation.
 */
export function proposerTitre(texte: string, nomFichier: string | null = null): string {
  if (texte.trim() === '') return '';
  if (nomFichier !== null) {
    const titre = titreDepuisNom(nomFichier);
    if (titre !== '' && !NOM_GENERIQUE.test(titre)) return titre;
  }
  return titreDepuisTexte(texte);
}
